import React, { useMemo } from 'react';

export interface CodeDisplayProps {
  /** Raw code string (e.g. "RrLl"); each character is shown large with spacing. */
  code: string;
  className?: string;
}

function codeCharacters(code: string): string[] {
  return Array.from(String(code ?? '').trim());
}

/**
 * Read-only large display for secret codes (breast morse, etc.).
 * Supports any length (3, 4, or more characters).
 */
const CodeDisplay: React.FC<CodeDisplayProps> = ({ code, className = '' }) => {
  const chars = useMemo(() => codeCharacters(code), [code]);

  if (chars.length === 0) {
    return (
      <div
        className={`code-display code-display--empty ${className}`.trim()}
        data-testid="code-display-empty"
        role="group"
        aria-label="No code"
      >
        <span className="code-display-placeholder" aria-hidden>
          —
        </span>
      </div>
    );
  }

  const ariaLabel = `Code: ${chars.join(' ')}`;

  return (
    <div
      className={`code-display ${className}`.trim()}
      data-testid="code-display"
      role="group"
      aria-label={ariaLabel}
    >
      {chars.map((ch, index) => (
        <span
          key={`${index}-${ch}`}
          className={`code-display-char${/[A-Z]/.test(ch) ? ' code-display-char--upper' : ''}`}
          aria-hidden
        >
          {ch}
        </span>
      ))}
    </div>
  );
};

export default CodeDisplay;
